import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageHead } from "../GtaxShell";
import { ALL_TIERS, usd } from "../data";
import { CLAUSES } from "../legal";
import { KycPanel } from "./KycPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gtax`;
const FIELDS = [["full_name", "Full legal name", "text"], ["email", "Email", "email"], ["phone", "Phone / WhatsApp", "tel"], ["country", "Country of residence", "text"]];

const Steps = ({ step }) => (
  <div className="flex gap-3 mb-12 text-xs tracking-widest uppercase" data-testid="gtax-access-steps">
    {["Your details", "Digital agreement", "Identity check", "Settlement"].map((s, i) => (
      <div key={s} className={`flex-1 pt-4 border-t-2 ${i <= step ? "border-[color:var(--gold)] text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--mute)]"}`}>
        {String(i + 1).padStart(2, "0")} · {s}
      </div>
    ))}
  </div>
);

const Label = ({ children }) => <label className="block text-xs tracking-widest uppercase text-[color:var(--mute)]">{children}</label>;

const Details = ({ f, set, next }) => {
  const tier = ALL_TIERS.find((t) => t.id === f.tier);
  const ok = FIELDS.every(([k]) => f[k].trim()) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) && f.wallet.trim().length >= 10 && Number(f.amount) >= tier.min;
  return (
    <div className="space-y-8" data-testid="gtax-access-details">
      <div className="grid md:grid-cols-2 gap-8">
        {FIELDS.map(([k, l, t]) => (
          <div key={k}><Label>{l}</Label><input type={t} value={f[k]} onChange={set(k)} className="w-full py-3 text-lg" data-testid={`gtax-access-${k}`} /></div>
        ))}
        <div>
          <Label>Membership tier</Label>
          <select value={f.tier} onChange={set("tier")} className="w-full p-3 mt-2 text-lg" data-testid="gtax-access-tier">
            {ALL_TIERS.map((t) => <option key={t.id} value={t.id}>{`${t.name} · from ${usd(t.min)}`}</option>)}
          </select>
        </div>
        <div><Label>Intended allocation (USD)</Label><input type="number" value={f.amount} onChange={set("amount")} className="w-full py-3 text-lg" data-testid="gtax-access-amount" /></div>
      </div>
      <div><Label>Your redemption wallet address (same network you deposit from)</Label><input value={f.wallet} onChange={set("wallet")} className="w-full py-3 text-lg font-mono" data-testid="gtax-access-wallet" /></div>
      {Number(f.amount) < tier.min && <p className="text-sm text-[#A0523F]" data-testid="gtax-access-min-error">{tier.name} requires a minimum allocation of {usd(tier.min)}.</p>}
      <button disabled={!ok} onClick={next} className="gx-btn disabled:opacity-40" data-testid="gtax-access-next">Continue to agreement</button>
    </div>
  );
};

const Agreement = ({ f, accepted, toggle, sig, setSig, busy, submit, back }) => {
  const all = CLAUSES.every((c) => accepted.includes(c.id));
  const sigOk = sig.trim().toLowerCase() === f.full_name.trim().toLowerCase();
  return (
    <div className="space-y-8" data-testid="gtax-access-agreement">
      <p className="text-[color:var(--mute)]">Please read each document and confirm. Your acceptance, signature, timestamp and device details are recorded as an evidential record of this agreement (GTAX-MA-1.0).</p>
      <div className="space-y-5">
        {CLAUSES.map((c) => (
          <label key={c.id} className="flex gap-4 items-start cursor-pointer" data-testid={`gtax-clause-${c.id}`}>
            <input type="checkbox" className="gx-check" checked={accepted.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`gtax-clause-${c.id}-checkbox`} />
            <span>{c.label} <Link to={c.link} target="_blank" className="underline text-[color:var(--gold2)]">Read</Link></span>
          </label>
        ))}
      </div>
      <div>
        <Label>Type your full legal name to sign</Label>
        <input value={sig} onChange={(e) => setSig(e.target.value)} placeholder={f.full_name} className="w-full py-3 gx-serif italic text-3xl" data-testid="gtax-access-signature" />
        {sig && !sigOk && <p className="mt-2 text-sm text-[#A0523F]" data-testid="gtax-access-sig-error">Signature must match: {f.full_name}</p>}
      </div>
      <div className="flex flex-wrap gap-4">
        <button onClick={back} className="gx-ghost" data-testid="gtax-access-back">Back</button>
        <button disabled={!all || !sigOk || busy} onClick={submit} className="gx-btn disabled:opacity-40" data-testid="gtax-access-sign">{busy ? "Recording…" : "Accept & sign"}</button>
      </div>
    </div>
  );
};

export default function GtaxAccess() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ full_name: "", email: "", phone: "", country: "", tier: ALL_TIERS.some((t) => t.id === params.get("tier")) ? params.get("tier") : "standard", amount: "", wallet: "" });
  const [accepted, setAccepted] = useState([]);
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [record, setRecord] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (id) => setAccepted(accepted.includes(id) ? accepted.filter((x) => x !== id) : [...accepted, id]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/agreements`, { ...f, amount: Number(f.amount), accepted, signature: sig });
      const rec = { ...data, email: f.email.trim().toLowerCase() };
      sessionStorage.setItem("gtax_member", JSON.stringify({ reference: rec.reference, email: rec.email }));
      setRecord(rec);
      setStep(2);
      toast.success("Agreement signed and recorded.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Could not record your agreement.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="gtax-access-page">
      <PageHead eyebrow="Request access" title="Membership, in four steps." sub="Provide your details, sign the digital Membership Agreement and verify your identity. Settlement details are released once your identity is approved." />
      <section className="pb-28">
        <div className="gx-wrap max-w-4xl">
          <Steps step={step} />
          <div className="gx-card p-8 md:p-12">
            {step === 0 && <Details f={f} set={set} next={() => setStep(1)} />}
            {step === 1 && <Agreement f={f} accepted={accepted} toggle={toggle} sig={sig} setSig={setSig} busy={busy} submit={submit} back={() => setStep(0)} />}
            {step === 2 && record && <KycPanel record={record} />}
          </div>
        </div>
      </section>
    </div>
  );
}
